import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Package, Truck, ArrowLeft, Maximize2, MapPin, Tag, X as XIcon, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import SEO from '@/components/SEO';
import { useCurrency } from '@/context/RegionContext';
import { useRegion } from '@/context/RegionContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

function getOrientation(w, h) {
  if (!w || !h) return 'portrait';
  const ratio = w / h;
  if (ratio >= 0.9 && ratio <= 1.1) return 'square';
  if (ratio > 1.1) return 'landscape';
  return 'portrait';
}

function getRecommendedSizes(sizes, orientation) {
  // Filter sizes matching the artwork's orientation
  const matching = {};
  Object.entries(sizes).forEach(([key, val]) => {
    if (val.orientation === orientation) matching[key] = val;
  });
  // Always include square as an option too
  if (orientation !== 'square') {
    Object.entries(sizes).forEach(([key, val]) => {
      if (val.orientation === 'square') matching[key] = val;
    });
  }
  return matching;
}

const ORIENTATION_LABELS = {
  portrait: 'Portrait',
  landscape: 'Landscape',
  square: 'Square',
};

/** Lazy-loaded smart-matted print preview — fetches a 72 DPI server render */
const PrintPreviewImage = ({ artworkId, selectedSize, regionKey, fallbackUrl }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const src = useMemo(
    () => `${API}/artworks/${artworkId}/print-image?size=${selectedSize}&region=${regionKey || 'india'}&preview=true`,
    [artworkId, selectedSize, regionKey]
  );

  useEffect(() => {
    setLoading(true);
    setError(false);
    const img = new Image();
    img.onload = () => { setPreviewUrl(src); setLoading(false); };
    img.onerror = () => { setError(true); setLoading(false); };
    img.src = src;
    return () => { img.onload = null; img.onerror = null; };
  }, [src]);

  if (loading) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" data-testid="print-preview-loading">
        <Loader2 size={20} className="animate-spin text-muted-foreground" />
        <span className="text-[10px] text-muted-foreground font-sans">Rendering preview...</span>
      </div>
    );
  }

  return (
    <img
      src={error ? fallbackUrl : previewUrl}
      alt="Print preview"
      className="pointer-events-none select-none w-full h-full"
      data-testid="print-preview-image"
      draggable="false"
      onContextMenu={(e) => e.preventDefault()}
      style={{ objectFit: error ? 'contain' : 'cover' }}
    />
  );
};

const PrintConfigurator = () => {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const { regionKey, region, isIndia } = useRegion();
  const [artwork, setArtwork] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedPaper, setSelectedPaper] = useState('matte');
  const [selectedFrame, setSelectedFrame] = useState('none');
  const [quantity, setQuantity] = useState(1);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    address_line1: '', address_line2: '', city: '', state: '', pincode: '',
    country: isIndia ? 'India' : ''
  });
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);

  useEffect(() => {
    let stale = false;  // set when region/artwork changes before this request finishes
    const fetchData = async () => {
      try {
        const artRes = await axios.get(`${API}/artworks`);
        if (stale) return;
        const found = artRes.data.find(a => a.id === artworkId);
        if (!found) { navigate('/gallery'); return; }
        setArtwork(found);

        // Fetch pricing with artwork_id for aspect ratio filtering
        const priceRes = await axios.get(`${API}/print-pricing`, {
          params: { region: regionKey || 'india', artwork_id: artworkId }
        });
        if (stale) return;
        setPricing(priceRes.data);

        // Auto-select the first sharp exact-fit size, else the first sharp size.
        // Sizes with available === false would print blurry (image resolution too low) and can't be chosen.
        const orient = getOrientation(found.image_width, found.image_height);
        const recommended = getRecommendedSizes(priceRes.data.sizes, orient);
        const usable = Object.entries(recommended).filter(([, v]) => v.available !== false);
        const exactFit = usable.find(([, v]) => v.fit === 'exact');
        const firstKey = exactFit ? exactFit[0] : usable[0]?.[0];
        setSelectedSize(firstKey || null);  // reset on region change too, so a now-invalid size isn't kept

        // Auto-select first available paper for this catalog
        const firstPaper = Object.keys(priceRes.data.papers)[0];
        if (firstPaper) setSelectedPaper(firstPaper);
      } catch (e) {
        console.error(e);
        toast.error('Failed to load artwork');
      } finally { if (!stale) setLoading(false); }
    };
    fetchData();
    return () => { stale = true; };
  }, [artworkId, navigate, regionKey]);

  // Auto-switch frame if current selection isn't available for this size
  useEffect(() => {
    if (selectedSize && pricing?.available_frames?.[selectedSize]) {
      const allowed = pricing.available_frames[selectedSize];
      if (!allowed.includes(selectedFrame)) {
        setSelectedFrame(allowed[0] || 'none');
      }
    }
  }, [selectedSize, pricing, selectedFrame]);

  if (loading || !pricing || !artwork) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="skeleton w-64 h-8 rounded" />
      </div>
    );
  }

  // No size can be printed sharply from the current image file
  if (!selectedSize) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4 flex items-center justify-center" data-testid="prints-unavailable">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-2xl sm:text-3xl font-light mb-3">Prints coming soon</h1>
          <p className="font-sans text-sm text-muted-foreground mb-6">
            We're preparing a high-resolution version of "{artwork.title}" so its prints come out sharp.
            Please check back soon, or contact us if you'd like to be told when it's ready.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => navigate(-1)} className="px-4 py-2 rounded-full border border-border text-sm hover:border-accent">Back</button>
            <button onClick={() => navigate('/contact')} className="px-4 py-2 rounded-full bg-accent text-white text-sm">Contact us</button>
          </div>
        </div>
      </div>
    );
  }

  const orientation = getOrientation(artwork.image_width, artwork.image_height);
  const recommendedSizes = getRecommendedSizes(pricing.sizes, orientation);
  const displaySizes = recommendedSizes;

  // Get available frames for the selected size — from catalog's available_frames map
  const availableFrameKeys = pricing.available_frames?.[selectedSize] || Object.keys(pricing.frames);
  const availableFrames = {};
  availableFrameKeys.forEach(key => {
    if (pricing.frames[key]) availableFrames[key] = pricing.frames[key];
  });

  // Ensure selectedSize has pricing
  const sizeHasPricing = pricing.base_prices[selectedSize] && pricing.frame_prices[selectedSize];
  const basePrice = sizeHasPricing ? pricing.base_prices[selectedSize][selectedPaper] : 0;
  const framePrice = sizeHasPricing ? pricing.frame_prices[selectedSize][selectedFrame] : 0;
  const unitPrice = basePrice + framePrice;
  const subtotal = unitPrice * quantity;
  const discountAmount = appliedDiscount ? appliedDiscount.discount_amount : 0;
  const total = subtotal - discountAmount;

  const handleApplyPrintDiscount = async () => {
    if (!discountCode.trim()) return;
    setApplyingDiscount(true);
    try {
      const res = await axios.post(`${API}/discounts/validate`, { code: discountCode.trim(), subtotal });
      setAppliedDiscount(res.data);
      toast.success(`Discount applied: ${res.data.label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally { setApplyingDiscount(false); }
  };

  const handleRemovePrintDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const handleOrder = async () => {
    if (!form.customer_name || !form.customer_email || !form.customer_phone || !form.address_line1 || !form.city || !form.state || !form.pincode) {
      toast.error('Please fill all required fields');
      return;
    }
    setIsProcessing(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error('Payment gateway failed'); setIsProcessing(false); return; }

      const res = await axios.post(`${API}/print-orders/create`, {
        artwork_id: artworkId,
        size: selectedSize,
        paper: selectedPaper,
        frame: selectedFrame,
        region: regionKey || 'india',
        quantity,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        shipping_address: {
          address_line1: form.address_line1, address_line2: form.address_line2,
          city: form.city, state: form.state, pincode: form.pincode, country: form.country
        },
        discount_code: appliedDiscount?.code || '',
      });

      const orderCurrency = isIndia ? 'INR' : 'USD';
      const options = {
        key: RAZORPAY_KEY,
        amount: res.data.amount * 100,
        currency: orderCurrency,
        name: 'Gangadhar Som - Art Prints',
        description: `${pricing.sizes[selectedSize].label} ${pricing.papers[selectedPaper].label} Print`,
        order_id: res.data.razorpay_order_id,
        handler: async (resp) => {
          try {
            await axios.post(`${API}/orders/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature
            });
            toast.success('Order placed! Your print will be shipped soon.');
            navigate('/gallery');
          } catch { toast.error('Payment verification failed'); }
        },
        prefill: { name: form.customer_name, email: form.customer_email, contact: form.customer_phone },
        theme: { color: '#D97706' },
        modal: { ondismiss: () => { setIsProcessing(false); toast.error('Payment cancelled'); } }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      console.error(e);
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to create order');
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="print-configurator-page">
      <SEO title={`Order Print - ${artwork.title} | Gangadhar Som`} noindex />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-accent mb-4 transition-colors">
          <ArrowLeft size={16} /> Back
        </button>

        <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-light tracking-tight mb-8 sm:mb-12">
          Order Art Print
        </h1>

        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left: Live Print Preview */}
          <div>
            <div className="artwork-frame bg-card sticky top-24 flex items-center justify-center p-4 sm:p-8" style={{ minHeight: 300 }}>
              <div className="relative w-full flex items-center justify-center">
                <div className="relative inline-block"
                  style={selectedFrame !== 'none' ? {
                    border: selectedFrame === 'black' ? '8px solid #222' : selectedFrame === 'white' ? '8px solid #f5f5f0' : selectedFrame === 'natural' ? '8px solid #b08455' : '8px solid #b08455',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  } : {}}>
                  {/* Smart-matted print preview — server-rendered with edge-color background */}
                  <div className="relative" data-testid="print-preview-container" style={{
                    width: (() => {
                      const sz = pricing.sizes[selectedSize];
                      if (!sz) return 400;
                      const maxW = 500, maxH = 450;
                      const scale = Math.min(maxW / sz.width, maxH / sz.height);
                      return sz.width * scale;
                    })(),
                    height: (() => {
                      const sz = pricing.sizes[selectedSize];
                      if (!sz) return 400;
                      const maxW = 500, maxH = 450;
                      const scale = Math.min(maxW / sz.width, maxH / sz.height);
                      return sz.height * scale;
                    })(),
                    overflow: 'hidden',
                    background: '#f8f7f4',
                  }}>
                    <PrintPreviewImage
                      artworkId={artworkId}
                      selectedSize={selectedSize}
                      regionKey={regionKey}
                      fallbackUrl={artwork.image_url}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-2">
              <Eye size={11} className="text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground font-sans tracking-wide">Live print preview with smart matting</span>
            </div>
            <p className="text-center font-serif text-lg sm:text-xl mt-3">{artwork.title}</p>
            {artwork.year && <p className="text-center text-sm text-muted-foreground">{artwork.year}</p>}
            {artwork.image_width && artwork.image_height && (
              <p className="text-center text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1.5" data-testid="image-dimensions">
                <Maximize2 size={12} />
                {artwork.image_width} x {artwork.image_height}px
                <span className="px-1.5 py-0.5 bg-accent/10 text-accent rounded-full text-[10px] font-medium uppercase">
                  {ORIENTATION_LABELS[orientation]}
                </span>
              </p>
            )}
          </div>

          {/* Right: Configuration */}
          <div className="space-y-6">
            {step === 1 ? (
              <>
                {/* Size Selection */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-sans uppercase tracking-[0.15em] text-xs font-semibold text-muted-foreground">
                      Select Size
                      <span className="ml-2 text-[10px] text-accent font-normal normal-case">
                        {ORIENTATION_LABELS[orientation].toLowerCase()} sizes
                      </span>
                    </h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(displaySizes).map(([key, val]) => {
                      const unavailable = val.available === false;
                      return (
                        <button key={key} onClick={() => !unavailable && setSelectedSize(key)}
                          disabled={unavailable}
                          aria-disabled={unavailable}
                          title={unavailable ? 'Not available at this size: the image resolution is too low for a sharp print' : undefined}
                          data-testid={`size-${key}`}
                          className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all relative ${
                            unavailable ? 'border-border opacity-45 cursor-not-allowed'
                              : selectedSize === key ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
                          }`}>
                          <div className="flex items-center justify-between">
                            <p className="font-serif text-lg sm:text-xl font-light">{val.label}</p>
                            {unavailable ? (
                              <span className="text-[9px] px-1.5 py-0.5 bg-muted text-muted-foreground rounded-full font-medium" data-testid={`unavailable-${key}`}>
                                Not available
                              </span>
                            ) : val.fit === 'exact' ? (
                              <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-medium" data-testid={`fit-exact-${key}`}>
                                Perfect Fit
                              </span>
                            ) : val.fit === 'matted' ? (
                              <span className="text-[9px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full font-medium" data-testid={`fit-matted-${key}`}>
                                Matted
                              </span>
                            ) : null}
                          </div>
                          <p className="font-sans text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">
                            {val.orientation}
                          </p>
                          {pricing.base_prices[key] && (
                            <p className="font-sans text-xs text-muted-foreground mt-0.5">
                              from {formatPrice(Object.values(pricing.base_prices[key])[0])}
                            </p>
                          )}
                          {selectedSize === key && <Check size={16} className="text-accent mt-1" />}
                        </button>
                      );
                    })}
                  </div>
                  {Object.values(displaySizes).some(v => v.available === false) && (
                    <p className="text-[11px] text-muted-foreground mt-2" data-testid="size-quality-note">
                      Larger sizes are unavailable for this artwork because the image isn't high-resolution enough for a sharp print.
                    </p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5" data-testid="white-border-note">
                    <span className="inline-block w-3 h-3 rounded-sm border border-border bg-amber-50 flex-shrink-0" />
                    {pricing.artwork_aspect
                      ? 'Sizes marked "Matted" use smart color-matched borders sampled from the painting\'s edges'
                      : 'All prints use smart matting — color-matched borders sampled from the painting\'s edges'}
                  </p>
                </div>

                {/* Paper Selection */}
                <div>
                  <h3 className="font-sans uppercase tracking-[0.15em] text-xs font-semibold text-muted-foreground mb-3">Paper Type</h3>
                  <div className="space-y-2">
                    {Object.entries(pricing.papers).map(([key, val]) => (
                      <button key={key} onClick={() => setSelectedPaper(key)}
                        data-testid={`paper-${key}`}
                        className={`w-full p-3 rounded-lg border-2 text-left flex items-center justify-between transition-all ${
                          selectedPaper === key ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
                        }`}>
                        <div>
                          <p className="font-sans font-medium text-sm sm:text-base">{val.label}</p>
                          <p className="font-sans text-xs text-muted-foreground">{val.description}</p>
                        </div>
                        {selectedPaper === key && <Check size={16} className="text-accent flex-shrink-0" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Frame Selection */}
                <div>
                  <h3 className="font-sans uppercase tracking-[0.15em] text-xs font-semibold text-muted-foreground mb-3">Frame</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(availableFrames).map(([key, val]) => (
                      <button key={key} onClick={() => setSelectedFrame(key)}
                        data-testid={`frame-${key}`}
                        className={`p-3 rounded-lg border-2 text-left transition-all ${
                          selectedFrame === key ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
                        }`}>
                        <p className="font-sans font-medium text-sm">{val.label}</p>
                        <p className="font-sans text-xs text-muted-foreground">{val.description}</p>
                        {selectedFrame === key && <Check size={16} className="text-accent mt-1" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <h3 className="font-sans uppercase tracking-[0.15em] text-xs font-semibold text-muted-foreground mb-3">Quantity</h3>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 rounded-lg border border-border hover:border-accent flex items-center justify-center text-lg">-</button>
                    <span className="font-serif text-xl w-8 text-center">{quantity}</span>
                    <button onClick={() => setQuantity(Math.min(10, quantity + 1))} disabled={quantity >= 10} className="w-10 h-10 rounded-lg border border-border hover:border-accent flex items-center justify-center text-lg">+</button>
                  </div>
                </div>

                {/* Price Summary */}
                <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-2" data-testid="price-summary">
                  <div className="flex justify-between text-sm"><span>Print ({pricing.sizes[selectedSize]?.label})</span><span>{formatPrice(basePrice)}</span></div>
                  {framePrice > 0 && <div className="flex justify-between text-sm"><span>Frame ({pricing.frames[selectedFrame].label})</span><span>{formatPrice(framePrice)}</span></div>}
                  {quantity > 1 && <div className="flex justify-between text-sm"><span>Quantity</span><span>x{quantity}</span></div>}
                  <div className="flex justify-between text-sm text-muted-foreground"><span>Shipping</span><span>Calculated at checkout</span></div>
                  <hr className="border-border" />
                  <div className="flex justify-between font-serif text-xl sm:text-2xl">
                    <span>Total</span><span className="text-accent" data-testid="total-price">{formatPrice(total)}</span>
                  </div>
                </div>

                {/* Region & Shipping Info */}
                <div className="bg-card border border-border rounded-lg p-4 space-y-2" data-testid="shipping-info">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={14} className="text-accent" />
                    <span className="font-medium">{region?.flag} {region?.label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-accent/10 text-accent rounded-full uppercase">
                      via {region?.fulfillment}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Truck size={12} />
                    <span>Estimated delivery: {region?.delivery}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{region?.shipping?.label}</p>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => navigate(-1)}>
                    Back to Gallery
                  </Button>
                  <Button
                    className="flex-1 bg-accent hover:bg-accent/90"
                    onClick={() => setStep(2)}
                    data-testid="proceed-to-checkout"
                  >
                    <Package size={16} className="mr-2" /> Proceed to Checkout
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Checkout Form */}
                <div>
                  <h3 className="font-sans uppercase tracking-[0.15em] text-xs font-semibold text-muted-foreground mb-3">Contact Information</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label>Full Name *</Label><Input value={form.customer_name} onChange={e => setForm({...form, customer_name: e.target.value})} data-testid="checkout-name" /></div>
                    <div><Label>Email *</Label><Input type="email" value={form.customer_email} onChange={e => setForm({...form, customer_email: e.target.value})} data-testid="checkout-email" /></div>
                    <div className="sm:col-span-2"><Label>Phone *</Label><Input value={form.customer_phone} onChange={e => setForm({...form, customer_phone: e.target.value})} data-testid="checkout-phone" /></div>
                  </div>
                </div>

                <div>
                  <h3 className="font-sans uppercase tracking-[0.15em] text-xs font-semibold text-muted-foreground mb-3 flex items-center gap-2"><Truck size={14} /> Shipping Address</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2"><Label>Address Line 1 *</Label><Input value={form.address_line1} onChange={e => setForm({...form, address_line1: e.target.value})} data-testid="checkout-address1" /></div>
                    <div className="sm:col-span-2"><Label>Address Line 2</Label><Input value={form.address_line2} onChange={e => setForm({...form, address_line2: e.target.value})} /></div>
                    <div><Label>City *</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="checkout-city" /></div>
                    <div><Label>State *</Label><Input value={form.state} onChange={e => setForm({...form, state: e.target.value})} data-testid="checkout-state" /></div>
                    <div><Label>Pincode *</Label><Input value={form.pincode} onChange={e => setForm({...form, pincode: e.target.value})} data-testid="checkout-pincode" /></div>
                    <div>
                      <Label>Country</Label>
                      <select value={form.country} onChange={e => setForm({...form, country: e.target.value})}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                        data-testid="checkout-country">
                        {['India', 'USA', 'UK', 'Canada', 'Australia', 'Germany', 'France', 'Japan', 'Singapore', 'UAE', 'Other'].map(c => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-card border border-border rounded-lg p-4 space-y-2" data-testid="order-summary">
                  <p className="font-sans text-sm font-medium">Order Summary</p>
                  <p className="text-sm text-muted-foreground">
                    {artwork.title} — {pricing.sizes[selectedSize]?.label} {pricing.papers[selectedPaper].label}
                    {selectedFrame !== 'none' && ` + ${pricing.frames[selectedFrame].label}`}
                    {quantity > 1 && ` x${quantity}`}
                  </p>

                  {/* Discount Code */}
                  {!appliedDiscount ? (
                    <div className="flex gap-2 pt-2">
                      <div className="flex-1 relative">
                        <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          data-testid="print-discount-input"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          placeholder="Discount code"
                          className="pl-9 font-mono uppercase text-sm"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyPrintDiscount())}
                        />
                      </div>
                      <Button variant="outline" size="sm" onClick={handleApplyPrintDiscount} disabled={applyingDiscount}
                        data-testid="print-apply-discount-btn" className="text-xs">
                        {applyingDiscount ? '...' : 'Apply'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="print-discount-applied">
                      <div className="flex items-center gap-2">
                        <Tag size={14} className="text-green-600" />
                        <span className="font-mono text-sm font-medium text-green-800">{appliedDiscount.code}</span>
                        <span className="text-xs text-green-600">{appliedDiscount.label}</span>
                      </div>
                      <button onClick={handleRemovePrintDiscount} className="text-green-600 hover:text-green-800">
                        <XIcon size={14} />
                      </button>
                    </div>
                  )}

                  {appliedDiscount && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount</span>
                      <span>-{formatPrice(discountAmount)}</span>
                    </div>
                  )}
                  <p className="font-serif text-xl text-accent">{formatPrice(total)}</p>
                </div>

                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>
                    <ArrowLeft size={16} className="mr-2" /> Back
                  </Button>
                  <Button
                    className="flex-1 bg-accent hover:bg-accent/90"
                    onClick={handleOrder}
                    disabled={isProcessing}
                    data-testid="place-order-btn"
                  >
                    {isProcessing ? 'Processing...' : `Pay ${formatPrice(total)}`}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintConfigurator;
